import { Button } from "@chakra-ui/react"
import {
  EDITOR_LAYERING,
  EDITOR_MODE,
  useEditorStateSelector
} from "@pathwright/blocks-engine"
import View from "@pathwright/ui/src/components/ui/View"
import { useTranslate } from "@pathwright/ux/src/components/lng/useTranslate"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import Tooltip from "@pathwright/ux/src/components/tooltip/Tooltip"
import "element-closest"
import { motion } from "framer-motion"
import React from "react"
import styled from "styled-components"
import ContentPublishAction from "./ContentPublishAction"

const ControlsView = styled(View.Primary)<{ $headerIsSticky: boolean }>`
  z-index: ${EDITOR_LAYERING.CONTENT_EDITOR_CONTROLS};
  position: ${(p) => (p.$headerIsSticky ? "sticky" : "static")};
  top: 0;
  display: flex;
  align-items: baseline;
  justify-content: flex-end;
  width: 100%;
  padding: 10px;
  max-width: 1200px;
  opacity: 1;
`

const ControlsActions = styled(motion.div)`
  display: flex;
  align-items: center;
`

const ContentEditorControls = () => {
  const mode = useEditorStateSelector((state) => state.mode)
  const isPublished = useEditorStateSelector((state) => state.isPublished)
  const syncing = useEditorStateSelector((state) => state.syncing)
  const blocksAreEmpty = useEditorStateSelector(
    (state) => !state.blocks?.length
  )
  const onToggleMode = useEditorStateSelector((state) => state.handleToggleMode)

  const { tc } = useTranslate()

  const [headerIsSticky, setHeaderIsSticky] = React.useState(false)
  const stickyTriggerRef = React.useRef(null)

  React.useEffect(() => {
    if (!stickyTriggerRef.current) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.intersectionRatio === 0) {
          // Marker is completely OUT of viewport — header becomes sticky
          setHeaderIsSticky(true)
        } else {
          // Marker is visible — header stays static
          setHeaderIsSticky(false)
        }
      },
      {
        root: null, // observe relative to viewport
        threshold: [0] // trigger as soon as it leaves or enters
      }
    )

    observer.observe(stickyTriggerRef.current)

    return () => {
      observer.disconnect()
    }
  }, [stickyTriggerRef])

  const handleToggleMode = () => {
    // switch to edit/view mode
    onToggleMode()
  }

  return (
    <>
      <div
        // Used by the IntersectionObserver
        id="sticky-trigger-marker"
        style={{
          height: 1,
          marginTop: -1,
          pointerEvents: "none",
          visibility: "hidden"
        }}
        ref={stickyTriggerRef}
      />
      <ControlsView $headerIsSticky={headerIsSticky}>
        <ControlsActions
          className={`ControlsActions`}
          animate={{ opacity: blocksAreEmpty ? 0 : 1 }}
          transition={{ type: "spring", stiffness: 170, damping: 26 }}
        >
          <Tooltip
            title={
              mode === EDITOR_MODE.EDIT
                ? tc("Preview drafted Blocks changes.")
                : tc("Edit Blocks.")
            }
          >
            <Button
              onClick={handleToggleMode}
              color="PRIMARY_GRAY"
              bgColor="transparent"
              _hover={{
                bgColor: "blackAlpha.50"
              }}
              fontWeight="400"
              size="sm"
              fontSize="0.9rem"
              py="0.5rem"
              px="0.9rem"
              disabled={syncing && mode === EDITOR_MODE.EDIT}
              mr={1}
              css={{
                "& .chakra-button__icon": {
                  marginRight: "0.3rem"
                }
              }}
            >
              <Pathicon
                icon={mode === EDITOR_MODE.EDIT ? "eye" : "edit"}
                size="0.9rem"
                color="PRIMARY_GRAY"
              />
              {mode === EDITOR_MODE.EDIT ? tc("Preview") : tc("Edit")}
            </Button>
          </Tooltip>
          <ContentPublishAction
            needsPublish={!isPublished && !blocksAreEmpty}
          />
        </ControlsActions>
      </ControlsView>
    </>
  )
}

export default ContentEditorControls
